// Employer marketplace insights — k-anonymous aggregates only (the employer
// sees the forest, never a tree). Suppressed figures (-1) render as "—" with
// the reason, so absence of data is never mistaken for zero.
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { creditApi, type EmployerAnalytics } from '@/features/credit/api/creditApi';

const BAND_ORDER = ['building', 'bronze', 'silver', 'gold', 'platinum', 'diamond'];

const fmtCount = (n: number) => (n < 0 ? '—' : String(n));

export function EmployerInsightsCard() {
  const [data, setData] = useState<EmployerAnalytics | null>(null);

  useEffect(() => {
    creditApi.employerAnalytics().then(setData).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const suppressed = data.participants < 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Marketplace insights</CardTitle>
        <p className="text-xs text-muted-foreground">
          Company-level aggregates only — individual usage is never shown. Small groups appear as “—” to protect privacy.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Employees participating" value={fmtCount(data.participants)} />
          <Stat label="Active products" value={fmtCount(data.active_facilities)} />
          <Stat label="On-time collection" value={data.on_time_rate < 0 ? '—' : `${Math.round(data.on_time_rate * 100)}%`} />
          <Stat label="Needs attention" value={fmtCount(data.at_risk)} />
        </div>

        {!suppressed && Object.keys(data.band_distribution).length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Financial trust bands</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {BAND_ORDER.filter((b) => data.band_distribution[b]).map((b) => (
                <span key={b} className="rounded-full bg-muted px-2.5 py-1 text-xs text-foreground">
                  <span className="capitalize">{b}</span> · {data.band_distribution[b]}
                </span>
              ))}
            </div>
          </div>
        )}

        {data.interest_saved > 0 && (
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            Your people have saved <strong>{Math.round(data.interest_saved).toLocaleString()}</strong> in interest through early repayments.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/40 p-3">
      <p className="text-lg font-bold tabular-nums text-foreground">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}
